import { createFileRoute, useNavigate, useRouterState } from '@tanstack/react-router';
import * as React from 'react';
import {
  SPEC_ASK_SEARCH_KEY,
  SPEC_CHAT_SEARCH_KEY,
  readSpecAsk,
  readSpecChatId,
} from '../lib/spec-conversations.js';

/**
 * Lazy so the Spec shell (and `@assistant-ui/react` via the runtime hooks it
 * reads) stays out of the entry chunk. The route module itself is still named
 * in `routeTree.gen.ts`; only the page implementation is deferred.
 */
const SpecPage = React.lazy(() =>
  import('../components/spec/page.js').then((module) => ({ default: module.SpecPage })),
);

type SpecOpenChat = (conversationId: string, options?: { replace?: boolean }) => void;

export function useSpecChatIdFromRoute(): string | null {
  return useRouterState({ select: (state) => readSpecChatId(state.location.search) });
}

/**
 * The route reads the intent; the page applies it.
 *
 * Which chat is open is URL state rather than something the sidebar pushes into
 * the runtime before navigating. That ordering could not be made safe: this
 * page is lazy, so a chat opened by the sidebar landed while the page chunk was
 * still downloading, and the page's own "new chat on arrival" then wiped it.
 * Reading it here removes the race instead of re-timing it, and refresh, back,
 * and forward all land on the chat the URL names.
 */
export function SpecRouteComponent() {
  const chatId = useSpecChatIdFromRoute();
  const ask = useRouterState({ select: (state) => readSpecAsk(state.location.search) });
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (state) => state.location.pathname });

  // The route WRITES the selection as well as reading it. Every way to pick a
  // chat -- the sidebar's rows and the home screen's recents -- goes through
  // the URL, so refresh restores the thread and clearing the param is what
  // takes the member back to the empty home.
  const onOpenChat = React.useCallback<SpecOpenChat>(
    (conversationId, options) => {
      void navigate({
        to: pathname,
        search: { [SPEC_CHAT_SEARCH_KEY]: conversationId },
        replace: options?.replace ?? false,
      });
    },
    [navigate, pathname],
  );

  // Once the question is in the composer the link has done its job; a refresh must not put it back.
  const onAskTaken = React.useCallback(() => {
    void navigate({
      to: pathname,
      // Every other parsed param (full=1 reads back as a number) stays as it was.
      search: (prev) =>
        Object.fromEntries(
          Object.entries(prev).filter(([key]) => key !== SPEC_ASK_SEARCH_KEY),
        ) as typeof prev,
      replace: true,
    });
  }, [navigate, pathname]);

  return (
    <React.Suspense fallback={null}>
      <SpecPage chatId={chatId} onOpenChat={onOpenChat} ask={ask} onAskTaken={onAskTaken} />
    </React.Suspense>
  );
}

export const Route = createFileRoute('/spec')({
  component: SpecRouteComponent,
});
